/**
 * Komal OS - Payment Store
 * Manages the payments sheet, dashboard aggregates, and create/edit/delete actions.
 */

import { create } from 'zustand';
import * as paymentService from '../services/paymentService';

const usePaymentStore = create((set, get) => ({
  payments: [],
  // Filters of the last sheet fetch, reused when an add/edit/record/write-off/
  // delete refreshes the sheet - otherwise it silently reloaded unfiltered
  // while the page's dropdowns still showed the old selection.
  lastParams: {},
  dashboard: null,
  isLoading: false,
  isSubmitting: false,
  error: null,

  clearError: () => set({ error: null }),

  fetchPayments: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      set({ lastParams: params });
      const response = await paymentService.getPayments(params);
      set({ payments: response.data.payments || [], isLoading: false });
      return response.data.payments;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load payments';
      set({ error: message, isLoading: false });
      return [];
    }
  },

  fetchDashboard: async () => {
    try {
      const response = await paymentService.getPaymentsDashboard();
      set({ dashboard: response.data });
      return response.data;
    } catch (error) {
      return null;
    }
  },

  createPayment: async (data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await paymentService.createPayment(data);
      await Promise.all([get().fetchPayments(get().lastParams), get().fetchDashboard()]);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to record payment';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  updatePayment: async (id, data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await paymentService.updatePayment(id, data);
      await Promise.all([get().fetchPayments(get().lastParams), get().fetchDashboard()]);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update payment';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  deletePayment: async (id) => {
    set({ isSubmitting: true, error: null });
    try {
      await paymentService.deletePayment(id);
      await Promise.all([get().fetchPayments(get().lastParams), get().fetchDashboard()]);
      set({ isSubmitting: false });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete payment';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },
}));

export default usePaymentStore;
